import React, { useState, useRef } from 'react'

// Payoff table keyed by `${yourMove}${aiMove}` -> [yourPoints, aiPoints]
const PAYOFF = {
  CC: [3, 3],
  CD: [0, 5],
  DC: [5, 0],
  DD: [1, 1],
}

const ROUNDS = 12
// How many rounds of future the AI trades against the current one. regimeValue
// is a per-round estimate and roughly a dozen rounds remain, so this is a
// horizon weight, not a per-round discount -- above 1 is expected.
const FUTURE_WEIGHT = 1.2

// Laplace-smoothed tally of how you replied to each of the AI's moves.
const freshModel = () => ({ C: { C: 1, D: 1 }, D: { C: 1, D: 1 } })

const coopRate = (row) => row.C / (row.C + row.D)

// Rough value of a regime where you cooperate at rate q: the best the AI can
// average per round against that tendency.
const regimeValue = (q) => Math.max(3 * q, 5 * q + 1 * (1 - q))

/*
 * One-ply lookahead against a learned model of you. Defecting always pays more
 * this round (5 > 3, 1 > 0), so the AI cooperates only when its model says
 * defection pushes you into a worse regime — i.e. only against players who
 * actually retaliate. That tradeoff is the shadow of the future, and it is the
 * whole reason cooperation is stable in a repeated game.
 */
const decide = (model, aiLastMove) => {
  const p = coopRate(model[aiLastMove]) // P(you cooperate this round)
  const qC = coopRate(model.C) // your cooperation rate after the AI cooperates
  const qD = coopRate(model.D) // ...and after it defects

  const evC = p * 3 + (1 - p) * 0 + FUTURE_WEIGHT * regimeValue(qC)
  const evD = p * 5 + (1 - p) * 1 + FUTURE_WEIGHT * regimeValue(qD)

  return { move: evC >= evD ? 'C' : 'D', p, evC, evD }
}

const PrisonersDilemma = ({ onFinish }) => {
  const model = useRef(freshModel())
  const [history, setHistory] = useState([])
  const [score, setScore] = useState({ you: 0, ai: 0 })
  const [read, setRead] = useState(null)

  const aiLastMove = history.length ? history[history.length - 1].ai : 'C'
  const done = history.length >= ROUNDS

  const play = (yourMove) => {
    if (done) return

    const thinking = decide(model.current, aiLastMove)
    const [youPts, aiPts] = PAYOFF[yourMove + thinking.move]

    // Learn: your move was a reply to whatever the AI played last round.
    model.current[aiLastMove][yourMove] += 1

    const next = [...history, { you: yourMove, ai: thinking.move, youPts, aiPts }]
    const nextScore = { you: score.you + youPts, ai: score.ai + aiPts }

    setHistory(next)
    setScore(nextScore)
    setRead(thinking)

    if (next.length >= ROUNDS) {
      onFinish({
        game: 'Prisoner’s Dilemma',
        result: nextScore.you > nextScore.ai ? 'win' : nextScore.you < nextScore.ai ? 'loss' : 'draw',
        detail: `${nextScore.you}–${nextScore.ai}`,
      })
    }
  }

  const reset = () => {
    model.current = freshModel()
    setHistory([])
    setScore({ you: 0, ai: 0 })
    setRead(null)
  }

  return (
    <div className="game">
      <div className="game__board">
        <table className="payoff">
          <thead>
            <tr>
              <th />
              <th>AI cooperates</th>
              <th>AI defects</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th>You cooperate</th>
              <td className="cell-good">3 / 3</td>
              <td className="cell-bad">0 / 5</td>
            </tr>
            <tr>
              <th>You defect</th>
              <td className="cell-great">5 / 0</td>
              <td>1 / 1</td>
            </tr>
          </tbody>
        </table>

        <div className="tape">
          {Array.from({ length: ROUNDS }, (_, i) => {
            const r = history[i]
            return (
              <div key={i} className={`tape__col ${r ? 'is-played' : ''}`}>
                <span className={`pip pip--${r ? r.you : 'empty'}`}>{r ? r.you : ''}</span>
                <span className={`pip pip--${r ? r.ai : 'empty'}`}>{r ? r.ai : ''}</span>
              </div>
            )
          })}
        </div>
        <div className="tape__legend">
          <span>your move</span>
          <span>AI move</span>
        </div>

        <div className="game__actions">
          <button className="btn btn-primary" onClick={() => play('C')} disabled={done}>
            Cooperate
          </button>
          <button className="btn" onClick={() => play('D')} disabled={done}>
            Defect
          </button>
          <button className="btn btn-ghost" onClick={reset}>
            {done ? 'Play again' : 'Reset'}
          </button>
        </div>
      </div>

      <aside className="game__panel">
        <h4>Round {Math.min(history.length + 1, ROUNDS)} of {ROUNDS}</h4>
        <div className="scoreline">
          <div><span>You</span><strong>{score.you}</strong></div>
          <div><span>AI</span><strong>{score.ai}</strong></div>
        </div>

        <h5>What the AI thinks of you</h5>
        {read ? (
          <>
            <p className="readout">
              Predicted chance you cooperate: <strong>{(read.p * 100).toFixed(0)}%</strong>
            </p>
            <div className="ev">
              <div className={read.evC >= read.evD ? 'ev--pick' : ''}>
                <span>EV cooperate</span><strong>{read.evC.toFixed(2)}</strong>
              </div>
              <div className={read.evD > read.evC ? 'ev--pick' : ''}>
                <span>EV defect</span><strong>{read.evD.toFixed(2)}</strong>
              </div>
            </div>
            <p className="note">
              Your cooperation rate after it cooperates: {(coopRate(model.current.C) * 100).toFixed(0)}%
              {' · '}after it defects: {(coopRate(model.current.D) * 100).toFixed(0)}%
            </p>
          </>
        ) : (
          <p className="note">Make a move and the model starts forming a read.</p>
        )}

        <p className="note note--rule">
          Defection always pays more <em>this</em> round. The AI only cooperates when
          its model says defecting would push you into punishing it later. Be a
          pushover and it will farm you; retaliate and it will play nice.
        </p>

        {done && (
          <p className="verdict">
            {score.you > score.ai ? 'You finished ahead.' : score.you < score.ai ? 'The AI finished ahead.' : 'Dead even.'}
          </p>
        )}
      </aside>
    </div>
  )
}

export default PrisonersDilemma
